/**
 * transcripcion.js — v2
 *
 * Normaliza una transcripción y saca de ella lo que la pantalla de detalle
 * necesita: turnos con tiempos, quién habló, cuánto y en qué momentos.
 *
 * El backend ya normaliza transcripciones (v2/core/TranscriptNormalizer.js),
 * pero allí solo interesa el texto que se le manda al modelo. Aquí hace falta
 * algo más: los tiempos, para poder dibujar la línea de intervenciones y
 * calcular las palabras por minuto de cada persona.
 */

// Los nombres que usan Teams, Zoom y Azure para lo mismo... y los NUESTROS.
//
// `hablante`, `texto`, `inicio` y `fin` son los que escribe esta aplicacion al
// transcribir audio. Faltaban, y eso tenia una consecuencia que no se parecia
// en nada a su causa: una transcripcion de audio se guardaba como texto plano
// —«Persona 1: hola»—, al reabrirla se intentaba releer y, como ningun patron
// reconocia ese formato, salia con CERO hablantes. El acta acababa con un solo
// asistente y la pantalla decia «No se distingue quien habla» sobre un audio en
// el que Azure habia separado a las personas perfectamente.
const CLAVES_HABLANTE = ['speakerDisplayName', 'userDisplayName', 'speakerName', 'speaker', 'displayName', 'hablante'];
const CLAVES_TEXTO = ['text', 'content', 'transcript', 'caption', 'texto'];
const CLAVES_INICIO = ['startTime', 'startOffset', 'start', 'offset', 'time', 'timestamp', 'inicio'];
const CLAVES_FIN = ['endTime', 'endOffset', 'end', 'fin'];

/**
 * Convierte a segundos los muchos formatos de tiempo que llegan:
 *   "0:00:05.4470000"  (Teams)
 *   "05:12"            (mm:ss)
 *   "PT1M5S"           (ISO 8601)
 *   123 / "123"        (segundos; si es muy grande, milisegundos)
 */
export function aSegundos(valor) {
  if (valor == null || valor === '') return null;

  if (typeof valor === 'number') {
    if (!isFinite(valor)) return null;
    // Un número muy grande no son segundos de una reunión: son milisegundos.
    return valor > 86400 ? valor / 1000 : valor;
  }

  const s = String(valor).trim();
  if (!s) return null;

  // hh:mm:ss(.mmm) o mm:ss(.mmm)
  if (/^\d{1,2}:\d{1,2}(:\d{1,2})?([.,]\d+)?$/.test(s)) {
    const partes = s.replace(',', '.').split(':').map(Number);
    return partes.length === 3
      ? partes[0] * 3600 + partes[1] * 60 + partes[2]
      : partes[0] * 60 + partes[1];
  }

  // ISO 8601: PT1H2M3.5S
  const iso = s.match(/^P?T?(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?$/i);
  if (iso && (iso[1] || iso[2] || iso[3])) {
    return (Number(iso[1]) || 0) * 3600 + (Number(iso[2]) || 0) * 60 + (Number(iso[3]) || 0);
  }

  const n = Number(s);
  return isFinite(n) ? (n > 86400 ? n / 1000 : n) : null;
}

/** Segundos → "mm:ss" o "h:mm:ss", como los sellos de tiempo de la minuta. */
export function comoReloj(segundos) {
  if (segundos == null || !isFinite(segundos)) return '';
  const total = Math.max(0, Math.round(segundos));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const dd = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${dd(m)}:${dd(s)}` : `${dd(m)}:${dd(s)}`;
}

function primerTexto(obj, claves) {
  for (const clave of claves) {
    const valor = obj[clave];
    if (typeof valor === 'string' && valor.trim()) return valor.trim();
    if (typeof valor === 'number') return valor;
  }
  return null;
}

function extraerLista(datos) {
  if (Array.isArray(datos)) return datos;
  if (!datos || typeof datos !== 'object') return null;
  // `turnos` es el nuestro y va el primero: es el que mas veces se relee.
  for (const clave of ['turnos', 'transcriptItems', 'phrases', 'entries', 'items', 'events', 'segments']) {
    if (Array.isArray(datos[clave])) return datos[clave];
  }
  return null;
}

/**
 * Reconstruye turnos a partir de texto plano con el formato que escribe v1:
 *   [0:01:23] Nombre:
 *   lo que dijo
 */
function turnosDeTextoPlano(texto) {
  const patron = /^\[([^\]]+)\]\s*([^:\n]{1,80}):\s*$/;
  const lineas = texto.split(/\r?\n/);
  const turnos = [];
  let actual = null;

  for (const linea of lineas) {
    const m = linea.match(patron);
    if (m) {
      if (actual) turnos.push(actual);
      actual = { hablante: m[2].trim(), texto: '', inicio: aSegundos(m[1]), fin: null };
    } else if (actual) {
      actual.texto += (actual.texto ? '\n' : '') + linea;
    }
  }
  if (actual) turnos.push(actual);

  return turnos
    .map(t => ({ ...t, texto: t.texto.trim() }))
    .filter(t => t.texto);
}

/**
 * ¿Esto es un archivo de subtítulos?
 *
 * Basta con la flecha de los tiempos. La cabecera `WEBVTT` no vale como única
 * señal porque el .srt no la lleva, y los dos formatos son la misma idea con
 * distinto separador decimal.
 */
export function pareceSubtitulos(texto) {
  const s = String(texto || '');
  if (/^\uFEFF?WEBVTT/.test(s.trimStart())) return true;
  return /^\s*\d{1,2}:\d{2}(:\d{2})?[.,]\d{1,3}\s*-->\s*\d{1,2}:\d{2}/m.test(s);
}

/**
 * Lee un .vtt o un .srt y saca sus turnos.
 *
 * Son el formato en el que la gente tiene de verdad sus transcripciones: es lo
 * que exporta Teams, lo que exporta Zoom y lo que sale de cualquier herramienta
 * de dictado. Sin esto, el archivo entero —cabecera, numeros de linea, flechas
 * de tiempo y etiquetas— se le entregaba al modelo tal cual, y el modelo tenia
 * que adivinar que la mitad de lo que leia no era conversacion.
 *
 * ── Lo que hay que quitar, y por que ─────────────────────────────────────
 *
 * · `WEBVTT`, `NOTE …` y los bloques `STYLE`/`REGION`: metadatos del formato.
 * · El numero de cue: en .srt va en su propia linea y se confunde con texto.
 * · `<v Nombre>`: asi marca Teams quien habla. Es la mejor fuente del nombre
 *   que hay, mejor que un «Nombre:» al principio de la linea, que tambien
 *   podria ser alguien citando a otro.
 * · `<00:00:01.000>` y `<c.color>`: marcas de karaoke y de estilo que algunos
 *   exportadores meten dentro de la frase misma.
 */
/**
 * ¿Lo que va antes de los dos puntos es el nombre de alguien?
 *
 * Hace falta preguntarlo porque media conversacion lleva dos puntos dentro:
 * «La conclusion es: hay que revisarlo» tiene exactamente la misma forma que
 * «Ana Lopez: hay que revisarlo». Sin esta comprobacion, el acta acababa con un
 * participante llamado «La conclusion es» y con la frase partida por la mitad.
 *
 * Se pide que sean pocas palabras y que empiecen en mayuscula, que es como se
 * escriben los nombres y como los exportan Teams y Zoom. Las particulas de los
 * apellidos no cuentan: «Maria de los Angeles Ruiz» es un nombre.
 *
 * Equivocarse no es simetrico. Si esto dice que no a un nombre de verdad, se
 * pierde a quien lo dijo y el texto queda entero; si dice que si a una frase,
 * se inventa una persona y se corta lo que dijo.
 */
const PARTICULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'da', 'di', 'van', 'von', 'el']);

export function pareceNombre(texto) {
  const limpio = String(texto || '').trim();
  if (!limpio || /[.!?,;]$/.test(limpio)) return false;

  const palabras = limpio.split(/\s+/);
  if (palabras.length > 5) return false;

  return palabras.every(p => PARTICULAS.has(p.toLowerCase()) || /^[^a-z\d]/.test(p[0] + ''));
}

export function turnosDeSubtitulos(texto) {
  const limpio = String(texto || '').replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const bloques = limpio.split(/\n{2,}/);
  const turnos = [];

  for (const bruto of bloques) {
    const lineas = bruto.split('\n').map(l => l.trim()).filter(Boolean);
    if (lineas.length === 0) continue;
    // Metadatos del formato: no son dialogo.
    if (/^(WEBVTT|NOTE|STYLE|REGION)\b/i.test(lineas[0])) continue;

    const iTiempo = lineas.findIndex(l => l.includes('-->'));
    if (iTiempo < 0) continue;

    const [desde, hasta] = lineas[iTiempo].split('-->').map(p => (p || '').trim());
    // Lo que sigue a los tiempos en la misma linea son ajustes de posicion
    // («align:start position:0%»), no parte del tiempo.
    const inicio = aSegundos(desde.split(/\s+/)[0].replace(',', '.'));
    const fin = aSegundos(hasta.split(/\s+/)[0].replace(',', '.'));

    let hablante = null;
    const dichas = [];

    for (const linea of lineas.slice(iTiempo + 1)) {
      let t = linea;

      // Así marca Teams quién habla.
      const voz = t.match(/^<v[^>]*?\s+([^>]+)>/i);
      if (voz) { hablante = voz[1].trim(); t = t.replace(/^<v[^>]*>/i, '').replace(/<\/v>/ig, ''); }

      t = t
        .replace(/<\d{1,2}:\d{2}(:\d{2})?[.,]\d{1,3}>/g, '')  // marcas de karaoke
        .replace(/<\/?[a-z][^>]*>/ig, '')                      // <c.amarillo>, <i>, <b>
        .trim();

      if (!t) continue;

      // «Nombre: lo que dijo», solo si no lo dijo ya una etiqueta <v>.
      if (!hablante && dichas.length === 0) {
        const dosPuntos = t.match(/^([^:]{2,60}):\s+(.*)$/);
        if (dosPuntos && dosPuntos[2] && pareceNombre(dosPuntos[1])) {
          hablante = dosPuntos[1].trim();
          t = dosPuntos[2].trim();
        }
      }

      if (t) dichas.push(t);
    }

    const dicho = dichas.join(' ').trim();
    if (dicho) turnos.push({ hablante, texto: dicho, inicio, fin });
  }

  return turnos;
}

/**
 * @param {string|Object|Array} datos - lo que devuelva SharePoint, Graph o una carga manual
 * @returns {{turnos: Array, hablantes: Array<string>, duracion: number|null, texto: string, origen: string}}
 */
export function normalizarTranscripcion(datos) {
  const vacia = { turnos: [], hablantes: [], duracion: null, texto: '', origen: 'vacío' };
  if (datos == null) return vacia;

  if (typeof datos === 'string') {
    const limpio = datos.trim();
    if (!limpio) return vacia;

    if (limpio.startsWith('{') || limpio.startsWith('[')) {
      try {
        return normalizarTranscripcion(JSON.parse(limpio));
      } catch {
        /* no era JSON; sigue como texto */
      }
    }

    // Los subtitulos primero: un .vtt tiene lineas con «Nombre:» que el lector
    // de texto plano reconoceria a medias, quedandose con el dialogo y tambien
    // con las flechas de tiempo.
    if (pareceSubtitulos(limpio)) {
      const cues = turnosDeSubtitulos(limpio);
      if (cues.length > 0) return desdeTurnos(cues, 'subtitulos');
    }

    const turnos = turnosDeTextoPlano(limpio);
    if (turnos.length > 0) return desdeTurnos(turnos, 'texto-con-marcas');
    return { ...vacia, texto: limpio, origen: 'texto-plano' };
  }

  const lista = extraerLista(datos);
  if (!lista) return vacia;

  const turnos = lista
    .map((item) => {
      if (typeof item === 'string') return { hablante: null, texto: item.trim(), inicio: null, fin: null };
      if (!item || typeof item !== 'object') return null;

      const texto = primerTexto(item, CLAVES_TEXTO);
      if (!texto) return null;

      return {
        hablante: primerTexto(item, CLAVES_HABLANTE),
        texto: String(texto).trim(),
        inicio: aSegundos(primerTexto(item, CLAVES_INICIO)),
        fin: aSegundos(primerTexto(item, CLAVES_FIN))
      };
    })
    .filter(t => t && t.texto);

  if (turnos.length === 0) return vacia;
  return desdeTurnos(turnos, 'estructurado');
}

/** Une turnos seguidos de la misma persona y cierra los tiempos que faltan. */
function desdeTurnos(turnos, origen) {
  // Teams parte una intervención larga en decenas de líneas de subtítulo. Para
  // leerlo como una conversación, se unen las contiguas del mismo hablante.
  const unidos = [];
  for (const turno of turnos) {
    const ultimo = unidos[unidos.length - 1];
    const mismoHablante = ultimo && ultimo.hablante === turno.hablante;
    const seguido = ultimo && turno.inicio != null && ultimo.fin != null &&
      turno.inicio - ultimo.fin < 3;

    if (mismoHablante && (seguido || turno.inicio == null)) {
      ultimo.texto += ' ' + turno.texto;
      if (turno.fin != null) ultimo.fin = turno.fin;
    } else {
      unidos.push({ ...turno });
    }
  }

  // Un turno sin fin dura hasta que empieza el siguiente.
  for (let i = 0; i < unidos.length; i++) {
    if (unidos[i].fin == null && unidos[i].inicio != null) {
      const siguiente = unidos[i + 1];
      unidos[i].fin = siguiente && siguiente.inicio != null
        ? siguiente.inicio
        : unidos[i].inicio + estimarSegundos(unidos[i].texto);
    }
  }

  const hablantes = [...new Set(unidos.map(t => t.hablante).filter(Boolean))];
  const duracion = unidos.reduce((max, t) => Math.max(max, t.fin || t.inicio || 0), 0) || null;
  const texto = unidos.map(t => (t.hablante ? `${t.hablante}: ${t.texto}` : t.texto)).join('\n');

  return { turnos: unidos, hablantes, duracion, texto, origen };
}

/**
 * Varias transcripciones de la MISMA reunión, leídas como una sola.
 *
 * ── Para qué ─────────────────────────────────────────────────────────────
 *
 * Porque una reunión no siempre deja una transcripción. Se cae el internet, se
 * corta la grabación, alguien la vuelve a iniciar — y lo que queda son dos
 * archivos de la misma junta. Generar el acta con uno solo da un acta a la que
 * le falta media reunión, y lo peor es que no se nota: el acta sale bien
 * escrita, completa de aspecto, y sin la mitad de los acuerdos.
 *
 * ── Lo que esto NO es ────────────────────────────────────────────────────
 *
 * No es pegar un texto detrás de otro. El problema son los TIEMPOS: la segunda
 * transcripción empieza otra vez en 00:00. Concatenada a secas, el acta
 * resultante tiene un minuto 42 seguido de un minuto 0, y entonces la pantalla
 * de hablantes, el reloj y el salto a un momento dejan de significar nada.
 *
 * Así que cada parte se corre hacia adelante lo que duraba la anterior.
 *
 * ── Y una advertencia que hay que decir en voz alta ──────────────────────
 *
 * Ese desplazamiento es APROXIMADO. Entre que se cortó una grabación y empezó
 * la siguiente pasó un rato real que los archivos no cuentan: pudo ser medio
 * minuto o diez. Los tiempos de la parte unida son «desde que empezó la
 * primera», no la hora del reloj. Para leer la conversación en orden sirve;
 * para citar el minuto exacto de la segunda parte, no. Por eso se devuelve
 * `aproximada`, y la pantalla lo dice.
 *
 * ── Por qué no se juntan los turnos a través de la costura ───────────────
 *
 * `desdeTurnos` une intervenciones seguidas de la misma persona, y al correr
 * los tiempos la última de una parte y la primera de la siguiente quedan
 * pegadas. Si se dejara, dos frases separadas por el corte —y por el rato que
 * nadie grabó— se leerían como una sola intervención continua. Cada parte se
 * normaliza por su cuenta y sus turnos se concatenan sin volver a juntarlos.
 *
 * @param {Array} piezas - textos crudos (o ya normalizados), EN EL ORDEN en
 *        que ocurrieron. El orden lo decide quien las sube: nada en un .vtt
 *        dice cuál fue antes, porque las dos empiezan en cero.
 * @returns {Object} la misma forma que `normalizarTranscripcion`, más:
 *        `partes` (qué entró en cada una) y `aproximada` (si hubo que correr
 *        tiempos).
 */
export function unirTranscripciones(piezas = []) {
  const lista = (Array.isArray(piezas) ? piezas : [piezas]).filter(p => p != null);

  const leidas = lista
    .map(p => (p && typeof p === 'object' && Array.isArray(p.turnos)
      ? p
      : normalizarTranscripcion(p)))
    .filter(t => t && (t.turnos.length > 0 || (t.texto || '').trim()));

  if (leidas.length === 0) {
    return { turnos: [], hablantes: [], duracion: null, texto: '', origen: 'vacío', partes: [], aproximada: false };
  }
  // Una sola no se «une»: se devuelve tal cual, con los tiempos que traía. Si
  // pasara por el desplazamiento quedaría marcada como aproximada sin serlo.
  if (leidas.length === 1) {
    return { ...leidas[0], partes: [resumenDeParte(leidas[0], 0)], aproximada: false };
  }

  const turnos = [];
  const partes = [];
  let desplazamiento = 0;
  let aproximada = false;

  leidas.forEach((parte, i) => {
    partes.push({ ...resumenDeParte(parte, i), desplazada: desplazamiento });

    if (parte.turnos.length === 0) {
      // Texto sin marcas de hablante ni tiempos: entra como un turno suelto.
      // No aporta tiempos, así que tampoco mueve el desplazamiento.
      turnos.push({ hablante: null, texto: parte.texto, inicio: null, fin: null });
      return;
    }

    for (const t of parte.turnos) {
      turnos.push({
        ...t,
        inicio: t.inicio == null ? null : t.inicio + desplazamiento,
        fin: t.fin == null ? null : t.fin + desplazamiento
      });
    }

    if (parte.duracion) {
      if (i > 0) aproximada = true;
      desplazamiento += parte.duracion;
    }
  });

  const hablantes = [...new Set(turnos.map(t => t.hablante).filter(Boolean))];
  const duracion = turnos.reduce((max, t) => Math.max(max, t.fin || t.inicio || 0), 0) || null;
  // Al modelo va la conversación entera y seguida. No se mete ninguna marca de
  // «aquí se cortó»: lo que se escriba aquí el modelo puede copiarlo dentro del
  // acta, y una minuta que diga «continuación de la grabación» es una minuta
  // contando cómo se hizo en vez de qué se dijo.
  const texto = turnos.map(t => (t.hablante ? `${t.hablante}: ${t.texto}` : t.texto)).join('\n');

  return { turnos, hablantes, duracion, texto, origen: 'unida', partes, aproximada };
}

function resumenDeParte(parte, i) {
  return {
    orden: i + 1,
    turnos: parte.turnos.length,
    duracion: parte.duracion || null,
    hablantes: parte.hablantes || [],
    palabras: palabras(parte.texto)
  };
}

/** Al ritmo normal de una conversación, ~2.5 palabras por segundo. */
function estimarSegundos(texto) {
  return Math.max(1, palabras(texto) / 2.5);
}

export function palabras(texto) {
  if (!texto) return 0;
  return texto.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Estadísticas por persona: es lo que alimenta las tarjetas de "Hablantes".
 *
 * PPM son palabras por minuto sobre el tiempo que esa persona habló, no sobre
 * la reunión entera: mide el ritmo al hablar, no cuánto participó. Para lo
 * segundo está `porcentaje`.
 *
 * @param {Array} turnos
 * @param {number|null} duracion - duración total, para situar las marcas
 */
export function estadisticasDeHablantes(turnos, duracion = null) {
  const total = duracion || turnos.reduce((max, t) => Math.max(max, t.fin || 0), 0) || 0;
  const porNombre = new Map();

  for (const turno of turnos) {
    const nombre = turno.hablante || 'Sin identificar';
    if (!porNombre.has(nombre)) {
      porNombre.set(nombre, {
        nombre,
        inicial: nombre.trim().charAt(0).toUpperCase() || '?',
        palabras: 0,
        segundos: 0,
        intervenciones: 0,
        marcas: []
      });
    }

    const stat = porNombre.get(nombre);
    const dura = turno.inicio != null && turno.fin != null ? Math.max(0, turno.fin - turno.inicio) : null;

    stat.palabras += palabras(turno.texto);
    stat.segundos += dura != null ? dura : estimarSegundos(turno.texto);
    stat.intervenciones += 1;

    if (total > 0 && turno.inicio != null) {
      stat.marcas.push({
        izquierda: Math.min(100, (turno.inicio / total) * 100),
        ancho: Math.max(0.4, Math.min(100, ((dura || 1) / total) * 100))
      });
    }
  }

  const tiempoHablado = [...porNombre.values()].reduce((s, x) => s + x.segundos, 0) || 1;

  return [...porNombre.values()]
    .map(stat => ({
      ...stat,
      ppm: stat.segundos > 0 ? Math.round(stat.palabras / (stat.segundos / 60)) : 0,
      porcentaje: Math.round((stat.segundos / tiempoHablado) * 100)
    }))
    .sort((a, b) => b.segundos - a.segundos);
}

/** Busca texto en los turnos y devuelve los que coinciden, ya resaltados. */
export function buscarEnTurnos(turnos, consulta) {
  const q = (consulta || '').trim().toLowerCase();
  if (!q) return turnos;
  return turnos.filter(t =>
    t.texto.toLowerCase().includes(q) ||
    (t.hablante || '').toLowerCase().includes(q)
  );
}

const transcripcion = {
  aSegundos,
  comoReloj,
  palabras,
  normalizarTranscripcion,
  unirTranscripciones,
  estadisticasDeHablantes,
  buscarEnTurnos,
  pareceSubtitulos,
  turnosDeSubtitulos,
  pareceNombre
};
export default transcripcion;
